// Utils
import { component } from 'picoapp';
import Swiper, {
  A11y,
  Autoplay,
  EffectFade,
  Manipulation,
  Mousewheel,
  Navigation,
  Scrollbar,
} from 'swiper';

import choozy from 'utils/choozy';

Swiper.use([A11y, Autoplay, Navigation, Manipulation, Mousewheel, Scrollbar, EffectFade]);

export default component(async (node) => {
  const {
    productItemScrollbar,
    scrollbar,
    slidersCount,
    sliderCountDynamic,
    sliderCountConstant,
    overlay,
  } = choozy(node);
  const classes = {
    thumbnail: {
      active: node.dataset.thumbnailActiveClass ?? 'carousel__dot--active',
    },
    thumbnailItem: {
      active: node.dataset.thumbnailActiveClass ?? 'carousel__dot--active',
    },
  };

  const scrollbarElement = scrollbar || productItemScrollbar;
  const autoplay = parseInt(node.dataset.autoplay || 0, 10);
  const speed = parseInt(node.dataset.speed || 300, 10);
  const hasCountdown = 'hasCountdown' in node.dataset;
  const spaceBetweenMobile = parseInt(node.dataset.spaceBetweenMobile || 0, 10);
  const spaceBetweenDesktop = parseInt(node.dataset.spaceBetweenDesktop || 0, 10);
  const paddingMobile = parseInt(node.dataset.paddingMobile || 0, 10);
  const paddingDesktop = parseInt(node.dataset.paddingDesktop || 0, 10);
  const useSectionPadding = node.dataset.useSectionPadding === 'true';
  const fadeEffect = node.dataset.fadeEffect === 'true';
  const enabledMobile = (node.dataset.enabledMobile || 'true') === 'true';
  const enabledDesktop = (node.dataset.enabledDesktop || 'true') === 'true';
  const changeClasses = node.dataset.changeClasses === 'true';
  const useNavigation = node.dataset.navigation !== 'false';
  const useMousewheel = node.dataset.mousewheel !== 'false';
  const allowTouchMove = (node.dataset.allowTouchMove || 'true') === 'true';

  let breakpoints = {
    1024: {
      enabled: enabledDesktop,
      spaceBetween: spaceBetweenDesktop,
      ...(useSectionPadding
        ? {
          slidesOffsetBefore: 40,
          slidesOffsetAfter: 40,
        }
        : {
          slidesOffsetBefore: paddingDesktop,
          slidesOffsetAfter: paddingDesktop,
        }),
    },
    ...(useSectionPadding
      ? {
        1280: {
          slidesOffsetBefore: 64,
          slidesOffsetAfter: 64,
        },
      }
      : {
        slidesOffsetBefore: paddingDesktop,
        slidesOffsetAfter: paddingDesktop,
      }),
  };

  if (hasCountdown) {
    breakpoints = {
      ...breakpoints,
      1024: {
        ...breakpoints[1024],
        autoplay: false,
      },
    };
  } else if (scrollbarElement === productItemScrollbar) {
    breakpoints = {
      ...breakpoints,
      1024: {
        ...breakpoints[1024],
        scrollbar: false,
      },
    };
  }

  const updateSliderCounter = (swiper) => {
    const activeSlideIndex = swiper.realIndex + 1;
    const totalSlides = swiper.slides.filter(
      (slide) => !slide.matches('[data-slider-toggler]'),
    ).length;

    sliderCountDynamic.textContent = activeSlideIndex;
    sliderCountConstant.textContent = totalSlides;
  };

  const navigation = useNavigation
    ? {
      nextEl:
        node.querySelector('.carousel__button--next:not(.carousel__button--product-item)') ||
        node.querySelector('.carousel__button--next'),
      prevEl:
        node.querySelector('.carousel__button--previous:not(.carousel__button--product-item)') ||
        node.querySelector('.carousel__button--previous'),
    }
    : false;

  const thumbnails = node.querySelectorAll('[data-thumbnail]');
  const thumbnailItems = node.querySelectorAll('[data-thumbnail-item]');
  const carousel = new Swiper(node, {
    enabled: enabledMobile,
    allowTouchMove,
    slidesPerView: 'auto',
    spaceBetween: spaceBetweenMobile,
    speed,
    a11y: {
      firstSlideMessage: theme.strings.aria.carousel.firstSlide,
      lastSlideMessage: theme.strings.aria.carousel.lastSlide,
      nextSlideMessage: theme.strings.aria.carousel.nextSlide,
      prevSlideMessage: theme.strings.aria.carousel.prevSlide,
    },
    ...(useSectionPadding
      ? {
        slidesOffsetBefore: 16,
        slidesOffsetAfter: 16,
      }
      : {
        slidesOffsetBefore: paddingMobile,
        slidesOffsetAfter: paddingMobile,
      }),
    ...(fadeEffect
      ? {
        effect: 'fade',
      }
      : {}),
    watchSlidesProgress: true,
    autoplay: autoplay
      ? {
        delay: autoplay,
      }
      : false,
    scrollbar: {
      el: scrollbarElement,
      draggable: scrollbarElement !== productItemScrollbar,
    },
    breakpoints,
    navigation,
    mousewheel: useMousewheel
      ? {
        forceToAxis: true,
      }
      : false,
    observer: !!slidersCount,
    on: {
      init: () => {
        window.lazySizes.autoSizer.checkElems();
      },
      slideChangeTransitionEnd: () => {
        window.lazySizes.autoSizer.checkElems();
      },
      observerUpdate: (swiper) => {
        if (slidersCount) {
          updateSliderCounter(swiper);
        }
      },
      ...(overlay
        ? {
          reachEnd: () => {
            overlay.classList.remove('show');
          },
          fromEdge: () => {
            overlay.classList.add('show');
          },
        }
        : {}),
    },
  });

  // Image split test start
  const skipToSecond = () => {
    const { speed } = carousel.params;
    carousel.params.speed = 0;
    carousel.update();
    carousel.slideTo(1);
    carousel.params.speed = speed;
    carousel.update();
  };

  if (node.closest('.product-item__images') !== null) {
    document.addEventListener('image-split-test', skipToSecond);
    if (window.imageSplitTest === true) {
      skipToSecond();
    }
  }
  // Image split test end

  if (slidersCount) {
    updateSliderCounter(carousel);

    carousel.on('slideChange', () => {
      updateSliderCounter(carousel);
    });
  }

  carousel.on('slideChange', () => {
    const currentVideo = carousel.slides[carousel.activeIndex]?.querySelector('video');
    const previousVideo = carousel.slides[carousel.previousIndex]?.querySelector('video');

    if (currentVideo) {
      currentVideo.currentTime = 0;
      currentVideo.play();
    }

    if (previousVideo) {
      previousVideo.pause();
    }

    if (node.dataset.productItem && window.theme.pageType === 'collection') {
      window.dataLayer.push({
        event: 'collection_card_image_click',
        collection_parameter: node.dataset.productItem,
      });
    }
  });

  if (thumbnails.length > 0) {
    carousel.on('slideChange', ({ realIndex, previousIndex }) => {
      thumbnails[previousIndex].classList.remove(classes.thumbnail.active);
      thumbnails[realIndex].classList.add(classes.thumbnail.active);
    });

    thumbnails.forEach((thumbnail) => {
      const index = parseInt(thumbnail.dataset.thumbnail, 10);

      thumbnail.addEventListener('click', () => {
        carousel.slideTo(index);
      });
    });
  }
  if (thumbnailItems.length > 0) {
    carousel.on('slideChange', ({ realIndex, previousIndex }) => {
      thumbnailItems[previousIndex].classList.remove(classes.thumbnailItem.active);
      thumbnailItems[realIndex].classList.add(classes.thumbnailItem.active);

      if (changeClasses) {
        const previousSlide = carousel.slides[previousIndex];
        const { sliderClasses: previousSlideClasses = [] } = previousSlide.dataset;
        const currentSlide = carousel.slides[realIndex];
        const { sliderClasses: currentSlideClasses = [] } = currentSlide.dataset;

        node.classList.remove(...previousSlideClasses.split(' '));
        node.classList.add(...currentSlideClasses.split(' '));
      }
    });

    thumbnailItems.forEach((thumbnailItem) => {
      const index = parseInt(thumbnailItem.dataset.thumbnailItem, 10);

      thumbnailItem.addEventListener('click', (e) => {
        e.preventDefault();
        carousel.slideTo(index);
      });
    });
  }
  if (useNavigation) {
    [navigation.nextEl, navigation.prevEl].forEach((button) => {
      button?.addEventListener('click', (event) => {
        event.stopPropagation();
      });
    });
  }

  // Custom Events
  node.addEventListener('replace-slides', (e) => {
    const { slides } = e.detail;

    carousel.removeAllSlides();
    carousel.appendSlide(slides);
    carousel.slideTo(0, 0);
  });
});
